import { useLazyQuery } from '@apollo/client/react';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import {
  ClientCertVerificationStatus,
  GetClientCertVerificationDocument,
} from '~/graphql/generated/graphql';

const IGNORE_PATHNAME = [
  '/signup',
  '/signin',
  '/client_certs/[clientCertId]/download',
  '/access_blocked',
  '/already_signed',
  '/404',
  '/422',
  '/500',
  '/login',
  '/proxy',
  '/checkin/[checkinOrganizationId]/entry',
  '/checkin/[checkinOrganizationId]/result',
  '/checkin/404',
  '/followup_messages/[publicFollowupMessageId]/questionnaire_sheet',
  '/followup_messages/404',
  '/outpatient_questionnaire/[outpatientQuestionnaireOrganizationId]/entry',
  '/outpatient_questionnaire/[outpatientQuestionnaireOrganizationId]/result/[outpatientQuestionnaireEntryId]',
  '/outpatient_questionnaire/404',
];

export const useClientCertVerification = () => {
  const { pathname, query } = useRouter();
  const [status, setStatus] = useState<ClientCertVerificationStatus>('none');
  const [loaded, setLoaded] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [getClientCertVerification, { data }] = useLazyQuery(GetClientCertVerificationDocument, {
    fetchPolicy: 'network-only',
  });
  const clientCert = data?.clientCertVerification?.clientCert;

  useEffect(() => {
    setLoaded(false);

    if (!IGNORE_PATHNAME.includes(pathname)) {
      // 証明書検証結果を取得
      (async () => {
        await getClientCertVerification();
      })();
    } else {
      setLoaded(true);
    }
  }, [getClientCertVerification, pathname]);

  useEffect(() => {
    if (data?.clientCertVerification?.status) {
      const status = data.clientCertVerification.status;
      setStatus(status);

      const url = data.clientCertVerification.url ? new URL(data.clientCertVerification.url) : null;
      if (url) {
        const params = new URLSearchParams();

        Object.entries(query).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((v) => params.append(key, v));
          } else if (value !== undefined) {
            params.append(key, value);
          }
        });
        const queryString = params.toString();
        const nextUrl = queryString
          ? `${url.origin}${pathname}?${queryString}`
          : `${url.origin}${pathname}`;
        setRedirectUrl(`${url}?next=${encodeURIComponent(nextUrl)}`);
      }

      setLoaded(true);
    }
  }, [
    data?.clientCertVerification?.clientCert,
    data?.clientCertVerification?.status,
    data?.clientCertVerification?.url,
    pathname,
    query,
  ]);

  return { status, redirectUrl, loaded, clientCert };
};
